import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Phone, Clock, MapPin, Mail, Send, Loader, X } from 'lucide-react';
import { FaWhatsapp, FaWaze, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { createPortal } from 'react-dom';

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
        onClick={onClose}
      />
      <div className="relative z-50 bg-white rounded-lg shadow-xl max-w-md w-full mx-auto">
        {children}
      </div>
    </div>,
    document.body
  );
};

const ContactPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [business, setBusiness] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [showHours, setShowHours] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    subject: ''
  });

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const businessDoc = await getDoc(doc(db, 'businessDetails', 'kioGnL9sTArCVO1W54OL'));
      setBusiness(businessDoc.data());

      const subjectsSnapshot = await getDocs(collection(db, 'subjects'));
      const subjectsData = subjectsSnapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().name
      }));
      
      // הוספת נושא "ייפוי כוח מתמשך" אם הוא לא קיים במסד הנתונים
      const powerOfAttorneyExists = subjectsData.some(subject => 
        subject.title.includes('ייפוי כוח מתמשך')
      );
      
      if (!powerOfAttorneyExists) {
        subjectsData.push({
          id: 'power-of-attorney',
          title: 'ייפוי כוח מתמשך'
        });
      }
      
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // אם נבחר נושא "ייפוי כוח מתמשך", נשמור את הכותרת במקום ה-ID
      const subjectToSave = formData.subject === 'power-of-attorney' 
        ? 'ייפוי כוח מתמשך' 
        : formData.subject;

      await addDoc(collection(db, 'leads'), {
        fullName: formData.fullName,
        phone: formData.phone,
        subject: subjectToSave,
        createdAt: new Date().toISOString()
      });
      navigate('/thank-you');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('אירעה שגיאה בשליחת הטופס. אנא נסו שוב.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!business) {
    return <div className="flex justify-center items-center min-h-screen">
      <Loader className="w-8 h-8 animate-spin text-blue-500" />
    </div>;
  }

  return (
    <div className="bg-gradient-to-r from-[#97a9b6] to-[#35434e]">
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">צור קשר</h1>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full"></div>
        </div>

        <div className="bg-gray-100 rounded-2xl shadow-2xl overflow-hidden">
          <div className="grid md:grid-cols-[auto_1fr]">
            <div className="p-4 md:p-6 bg-gray-50 border-r border-gray-200">
              <div className="flex flex-row md:flex-col gap-4 items-center">
                <a
                  href={`tel:${business.number}`}
                  className="flex flex-col items-center p-3 bg-white rounded-lg hover:shadow-md transition-all duration-300 group"
                  title="התקשר אלינו"
                >
                  <Phone className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform duration-300" />
                  <span className="mt-1 text-xs text-gray-600">טלפון</span>
                </a>

                <a
                  href={`mailto:${business.mail}`}
                  className="flex flex-col items-center p-3 bg-white rounded-lg hover:shadow-md transition-all duration-300 group"
                  title="שלח מייל"
                >
                  <Mail className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform duration-300" />
                  <span className="mt-1 text-xs text-gray-600">מייל</span>
                </a>

                <a
                  href={`https://waze.com/ul?q=${encodeURIComponent(business.address)}&navigate=yes`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center p-3 bg-white rounded-lg hover:shadow-md transition-all duration-300 group"
                  title="נווט אלינו"
                >
                  <FaWaze className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform duration-300" />
                  <span className="mt-1 text-xs text-gray-600">ניווט</span>
                </a>

                <button
                  onClick={() => setShowHours(true)}
                  className="flex flex-col items-center p-3 bg-white rounded-lg hover:shadow-md transition-all duration-300 group"
                  title="שעות פעילות"
                >
                  <Clock className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform duration-300" />
                  <span className="mt-1 text-xs text-gray-600">שעות</span>
                </button>

                <a
                   href="https://wa.me/972532788686" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center p-3 bg-white rounded-lg hover:shadow-md transition-all duration-300 group"
                  title="פתח צ'אט בוואטסאפ"
                >
                  <FaWhatsapp className="w-6 h-6 text-green-500 group-hover:scale-110 transition-transform duration-300" />
                  <span className="mt-1 text-xs text-gray-600">וואטסאפ</span>
                </a>
              </div>
            </div>

            <div className="p-8 md:p-10 bg-gray-100">
              <h2 className="text-2xl font-bold mb-8 text-gray-800 flex items-center gap-3">
                <Send className="w-6 h-6 text-blue-600" />
                השאירו פרטים
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    שם מלא
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 hover:border-blue-300"
                    placeholder="הכנס שם מלא"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    טלפון
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 hover:border-blue-300"
                    placeholder="הכנס מספר טלפון"
                    dir="rtl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    נושא הפנייה
                  </label>
                  <select
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 hover:border-blue-300 appearance-none bg-white"
                  >
                    <option value="">בחר נושא</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>
                        {subject.title}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`
                    w-full p-4 rounded-xl
                    flex items-center justify-center gap-3
                    ${loading 
                      ? 'bg-gray-400' 
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                    }
                    text-white font-medium transition-all duration-300
                    transform hover:scale-[1.02]
                    shadow-lg hover:shadow-xl
                  `}
                >
                  {loading ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>שלח פנייה</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        <Modal isOpen={showHours} onClose={() => setShowHours(false)}>
          <div className="p-6" dir="rtl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Clock className="w-6 h-6 text-blue-600" />
                <h3 className="text-2xl font-bold text-gray-800">שעות פעילות</h3>
              </div>
              <button
                onClick={() => setShowHours(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="font-medium">ראשון - חמישי</span>
                <span className="text-gray-600">18:00 - 09:00 </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="font-medium">שישי</span>
                <span className="text-gray-600">סגור</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="font-medium">שבת</span>
                <span className="text-gray-600">סגור</span>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ContactPage;